// src/components/personal/sections/SettingsSection/AppSettings/AppSettings.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/context/AuthContext';
import { api } from '@/services/api';
import { AppSettings as AppSettingsType, AppSettingsState } from './types';
import { useFormOptions } from './utils';
import { hasChanges, getChangedValues, initializeSettings } from './settings';
import AppSettingsForm from './AppSettingsForm';
import Message from '@/components/ui/Message';
import useMessage from '@/hooks/useMessage';
import MessageContainer from '@/components/ui/MessageContainer';

// Функция для безопасного сохранения в localStorage
const safelySetItem = (key: string, value: string | null): void => {
  try {
    if (value && value !== 'undefined') {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error(`Error setting localStorage item ${key}:`, e);
  }
};

const defaultSettings: AppSettingsType = {
  business_type: 'none',
  facebook_business_id: '',
  assistant_instructions: '',
  business_utc: '+06:00',
  business_email: '',
  business_email_for_assistant: false,
  business_currency: 'KZT',
  whatsapp_number: '',
  whatsapp_for_assistant: false,
  margin: 0.3, // Значение по умолчанию для маржи (30%)
  discount_rate: 0.12 // Значение по умолчанию для ставки дисконтирования (12%)
};

const AppSettings: React.FC = () => {
  // Загружаем несколько пространств имен для переводов
  const { t: tProfile } = useTranslation('profile');
  useTranslation('common');
  
  const { user, checkAuth } = useAuthContext(); // Используем checkAuth для обновления данных пользователя
  const { businessTypes, timeZones, currencies } = useFormOptions();
  const { message, showMessage, hideMessage, isVisible } = useMessage();
  
  const [state, setState] = useState<AppSettingsState>({
    settings: { ...defaultSettings },
    originalSettings: { ...defaultSettings },
    loading: true,
    saving: false,
    success: false,
    error: ''
  });
  
  // Загрузка данных
  useEffect(() => {
    if (user) {
      const initialSettings = initializeSettings(user, defaultSettings);
      
      setState(prev => ({
        ...prev,
        settings: initialSettings,
        originalSettings: initialSettings,
        loading: false
      }));
    }
  }, [user]);
  
  // Обработчик изменения полей формы
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    // Используем as для обхода проверки типов
    const input = event.target as any;
    const fieldName = input.name;
    let fieldValue;
    
    if (input.type === 'checkbox') {
      fieldValue = input.checked;
      console.log(`Чекбокс изменен: ${fieldName} = ${fieldValue} (тип: ${typeof fieldValue})`);
    } else {
      fieldValue = input.value;
    }
    
    setState(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        [fieldName]: fieldValue
      }
    }));
  };
  
  const handleSaveSettings = async (e: FormEvent) => {
    e.preventDefault();
    
    // Проверяем, есть ли изменения
    if (!hasChanges(state.settings, state.originalSettings)) {
      return;
    }
    
    if (!user || !user.id) return;
    
    try {
      setState(prev => ({ 
        ...prev, 
        saving: true,
        error: '',
        success: false
      }));
      
      // Получаем только измененные поля и создаем копию для возможной модификации
      const changedSettings = {...getChangedValues(state.settings, state.originalSettings)};
      
      // Готовим финальные данные для отправки
      // Убедимся, что булевы поля имеют правильные значения
      if ('business_email_for_assistant' in changedSettings) {
        changedSettings.business_email_for_assistant = 
          Boolean(changedSettings.business_email_for_assistant);
      }
      
      if ('whatsapp_for_assistant' in changedSettings) {
        changedSettings.whatsapp_for_assistant = 
          Boolean(changedSettings.whatsapp_for_assistant);
      }
      
      // Преобразуем числовые поля в числа
      if ('margin' in changedSettings) {
        changedSettings.margin = Number(changedSettings.margin);
      }
      
      if ('discount_rate' in changedSettings) {
        changedSettings.discount_rate = Number(changedSettings.discount_rate);
      }
      
      console.log('Сохраняем изменения настроек:', changedSettings);
      
      // Отправляем запрос на обновление профиля
      const response = await api.post(api.endpoints.auth.updateProfile, changedSettings);
      
      if (response && response.success) {
        // Обновляем данные пользователя через контекст аутентификации
        await checkAuth();
        
        // Получаем обновленный профиль
        const updatedProfile = await api.get(api.endpoints.auth.profile);
        
        // Убедимся, что булевы поля получены правильно
        if (updatedProfile) {
          // Принудительное приведение булевых полей к Boolean типу
          updatedProfile.business_email_for_assistant = 
            Boolean(updatedProfile.business_email_for_assistant);
          updatedProfile.whatsapp_for_assistant = 
            Boolean(updatedProfile.whatsapp_for_assistant);
          
          // Приведение числовых полей к Number типу
          updatedProfile.margin = Number(updatedProfile.margin);
          updatedProfile.discount_rate = Number(updatedProfile.discount_rate);
        }
        
        // Обновляем оригинальные и текущие настройки с данными с сервера
        const updatedSettings = initializeSettings(updatedProfile, defaultSettings);
        
        // Генерируем событие об изменении настроек, если изменилась валюта
        if (typeof window !== 'undefined' && changedSettings.business_currency) {
          // Сохраняем валюту в localStorage
          safelySetItem('businessCurrency', changedSettings.business_currency);
          
          // Создаем и отправляем пользовательское событие
          // Используем явную типизацию Event для совместимости с TypeScript
          const settingsEvent = new Event('business:settings:updated');
          (window as Window).dispatchEvent(settingsEvent);
          console.log('Отправлено событие об изменении настроек валюты:', changedSettings.business_currency);
        }
        
        setState(prev => ({
          ...prev,
          settings: updatedSettings,
          originalSettings: updatedSettings,
          saving: false,
          success: true
        }));
        
        // Показываем сообщение об успехе через хук useMessage
        showMessage('success', null, {
          translateKey: 'profile:settings_saved', // Обновлено: добавлен префикс пространства имен
          autoHide: true,
          autoHideTime: 3000
        });
        
        console.log('Настройки успешно сохранены и обновлены из профиля:', updatedSettings);
      } else {
        throw new Error(response.error || 'Ошибка сохранения настроек');
      }
    } catch (err: any) {
      console.error('Error saving app settings:', err);
      setState(prev => ({
        ...prev,
        saving: false,
        error: err.message || tProfile('settings_save_error')
      }));
      
      // Показываем сообщение об ошибке через хук useMessage
      showMessage('error', err.message || tProfile('settings_save_error'));
    }
  };
  
  if (state.loading) {
    return <Message type="info" translateKey="common:loading" icon={true} />; // Обновлено: добавлен префикс пространства имен
  }
  
  return (
    <>
      <AppSettingsForm
        settings={state.settings}
        businessTypes={businessTypes}
        timeZones={timeZones}
        currencies={currencies}
        saving={state.saving}
        success={state.success}
        error={state.error}
        onChange={handleInputChange}
        onSubmit={handleSaveSettings}
        hasChanges={hasChanges(state.settings, state.originalSettings)}
      />
      
      {/* Всплывающее сообщение через MessageContainer */}
      <MessageContainer
        type={message.type}
        message={message.content}
        translateKey={message.translateKey}
        values={message.values}
        isVisible={isVisible}
        onClose={hideMessage}
        position="top"
      />
    </>
  );
};

export default AppSettings;